import { Img } from '@/components/builder2/utils/Img';
import React from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, MessageCircle, Globe } from 'lucide-react';
import { TikTokIcon } from '../../icons/TikTokIcon';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { resolveFooterLink } from '@/utils/footerLinks';
import { gridFooterClasico, type ColumnasPorFila } from '@/utils/footerLayout';
import { redesConfiguradas } from '@/utils/socialLinks';

interface FooterLink {
  text: string;
  url: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  platform: 'facebook' | 'instagram' | 'twitter' | 'youtube' | 'linkedin' | 'tiktok' | 'whatsapp';
  url: string;
}

// Empty stays empty (icon hides). WhatsApp accepts a bare phone → https://wa.me/<digits>.
const normalizeSocialUrl = (platform: string, raw: string): string => {
  const v = (raw || '').trim();
  if (!v || v === '#') return '';
  if (platform === 'whatsapp' && !/^https?:\/\//i.test(v)) {
    const digits = v.replace(/[^\d]/g, '');
    return digits ? `https://wa.me/${digits}` : '';
  }
  // En TikTok lo natural es copiar el usuario, no la URL.
  if (platform === 'tiktok' && v.startsWith('@')) return `https://www.tiktok.com/${v}`;
  // Sin esquema el href sale relativo y el link cae dentro del propio sitio.
  if (!/^https?:\/\//i.test(v)) return `https://${v}`;
  return v;
};

interface FooterProps {
  columns?: FooterColumn[];
  bgColor?: string;
  textColor?: string;
  headingColor?: string;
  dividerColor?: string;
  socialIconBgColor?: string;
  showSocial?: boolean;
  socialLinks?: SocialLink[];
  copyrightText?: string;
  logoUrl?: string;
  /** Alto del logo en px. Default 40 = el `h-10` que tuvo siempre (ticket #57). */
  logoHeight?: number;
  /** Columnas de enlaces por fila. 'auto' se ajusta a las que haya. */
  columnsPerRow?: ColumnasPorFila;
  /** Columna con las redes cargadas en Configuración → General. Se apaga desde
   *  el panel del builder. Sin redes configuradas no se dibuja nada igual. */
  showSocialColumn?: boolean;
  socialColumnTitle?: string;
}

const SocialIcon = ({ platform, size = 20 }: { platform: string; size?: number }) => {
  switch (platform) {
    case 'facebook': return <Facebook size={size} />;
    case 'instagram': return <Instagram size={size} />;
    case 'twitter': return <Twitter size={size} />;
    case 'youtube': return <Youtube size={size} />;
    case 'linkedin': return <Linkedin size={size} />;
    case 'tiktok': return <TikTokIcon size={size} />;
    case 'whatsapp': return <MessageCircle size={size} />;
    // Una plataforma desconocida mostraba el logo de Facebook. Mejor un globo.
    default: return <Globe size={size} />;
  }
};

export const Footer = ({
  // Defaults en paridad con el admin: rutas reales, no anclas inexistentes.
  columns = [
    { title: 'Empresa', links: [{ text: 'Sobre nosotros', url: '/about' }, { text: 'Contacto', url: '/contact' }] },
    { title: 'Servicios', links: [{ text: 'Financiamiento', url: '/financing' }, { text: 'Consignaciones', url: '/consignments' }] },
    { title: 'Vehículos', links: [{ text: 'Ver catálogo', url: '/vehicles' }] },
  ],
  bgColor = '#111827',
  textColor = '#9ca3af',
  headingColor = '#ffffff',
  dividerColor = 'rgba(255, 255, 255, 0.1)',
  socialIconBgColor = 'rgba(255, 255, 255, 0.08)',
  showSocial = true,
  socialLinks = [
    { platform: 'facebook', url: '' },
    { platform: 'instagram', url: '' },
    { platform: 'whatsapp', url: '' },
  ],
  copyrightText = '',
  logoUrl = '',
  // Default en la firma: los árboles ya guardados no traen el prop, sale
  // undefined y cae acá. 40px es lo que rendía el `h-10` anterior.
  logoHeight = 40,
  columnsPerRow = 'auto',
  // Default en la FIRMA, no dentro de `columns`: los tenants que ya tienen su
  // árbol guardado no traen este prop, sale undefined y cae acá. Cero migración
  // del craft JSON. (Si viviera en `columns`, los ~89 con estructura propia no
  // la verían nunca.)
  showSocialColumn = true,
  socialColumnTitle = 'Redes sociales',
}: FooterProps) => {
  const { connectors } = useNode();
  const { client } = useClientStore();
  const { t } = useTranslation();

  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.options.enabled,
  }));

  // Pick logo based on bgColor luminance (solo si el builder no define uno)
  const logoDark = client?.logo_dark || '';
  const logoLight = client?.logo || '';
  const isDarkBg = (() => {
    const c = (bgColor || '').replace('#', '');
    if (c.length !== 6) return false;
    const r = parseInt(c.substring(0, 2), 16) / 255;
    const g = parseInt(c.substring(2, 4), 16) / 255;
    const b = parseInt(c.substring(4, 6), 16) / 255;
    return 0.299 * r + 0.587 * g + 0.114 * b < 0.4;
  })();
  const finalLogoUrl = logoUrl || (isDarkBg ? (logoDark || logoLight) : (logoLight || logoDark));

  const currentYear = new Date().getFullYear();
  const finalCopyrightText =
    copyrightText || `\u00A9 ${currentYear} ${client?.name || ''}. ${t('site.footer.rights')}`;

  const handleLinkClick = (e: React.MouseEvent, url: string) => {
    if (isEnabled) { e.preventDefault(); e.stopPropagation(); return; }
    if (url.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(url);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else {
        // No matching element on page — navigate to the route instead
        window.location.href = `/${url.slice(1)}`;
      }
    }
  };

  const handleSocialClick = (e: React.MouseEvent) => {
    if (isEnabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Los links de fábrica sin página real no se muestran (irían a un 404). Si a una
  // columna no le queda ninguno se descarta entera, para no dejar el título
  // flotando solo (le pasaba a "Legal"). En el editor se muestran todos, para
  // que el cliente pueda editarlos. El conteo resultante define el ancho del grid.
  const columnasVisibles = columns
    .map((column) => ({
      column,
      visibles: (column.links || [])
        .map((link) => ({ link, target: resolveFooterLink(link.url) }))
        .filter(({ target }) => isEnabled || !target.hidden),
    }))
    .filter(({ visibles }) => visibles.length > 0);

  // Columna de redes: sale de lo que la automotora cargó en Configuración →
  // General, sin volver a tipear nada en el builder.
  //
  // Se descartan las que YA están como iconito arriba (prop `socialLinks`, que
  // varios tenants cargaron a mano): si no, Sigma Autos mostraría Facebook dos
  // veces y encima con dos URLs distintas. Los iconos mandan porque son lo que
  // el cliente editó explícitamente en esta pantalla.
  const yaEnIconos = new Set(
    showSocial
      ? socialLinks
          .filter((s) => normalizeSocialUrl(s.platform, s.url))
          .map((s) => s.platform)
      : []
  );
  const redes = showSocialColumn
    ? redesConfiguradas(client).filter((r) => !yaEnIconos.has(r.platform))
    : [];

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{ backgroundColor: bgColor, color: textColor, position: 'relative' }}
      className='w-full'
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16'>
        <div
          className={gridFooterClasico(
            columnasVisibles.length,
            columnsPerRow,
            redes.length ? 1 : 0
          )}
        >
          {/* Logo / Brand column */}
          <div className='lg:col-span-1'>
            {finalLogoUrl ? (
              <Img variante="icono"
      src={finalLogoUrl}
      alt='Logo'
      className='w-auto mb-4 object-contain'
      style={{ height: `${logoHeight}px` }}
    />
            ) : (
              <h3 className='text-xl font-bold mb-4' style={{ color: headingColor }}>&nbsp;</h3>
            )}
            {showSocial && (() => {
              const visible = socialLinks
                .map((s) => ({ ...s, url: normalizeSocialUrl(s.platform, s.url) }))
                .filter((s) => s.url);
              if (visible.length === 0) return null;
              return (
                <div className='flex items-center gap-3 mt-4'>
                  {visible.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={handleSocialClick}
                      className='p-2 rounded-full transition-colors duration-200 hover:opacity-80'
                      style={{ color: textColor, backgroundColor: socialIconBgColor }}
                    >
                      <SocialIcon platform={social.platform} size={18} />
                    </a>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Link columns */}
          {columnasVisibles.map(({ column, visibles }, colIndex) => {
            return (
              <div key={colIndex}>
                <h4 className='text-sm font-semibold uppercase tracking-wider mb-4' style={{ color: headingColor }}>
                  {column.title}
                </h4>
                <ul className='space-y-3'>
                  {visibles.map(({ link, target }, linkIndex) => (
                    <li key={linkIndex}>
                      <a
                        href={target.href}
                        {...(target.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        onClick={(e) => handleLinkClick(e, target.href)}
                        className='text-sm transition-colors duration-200 hover:opacity-80'
                        style={{ color: textColor }}
                      >
                        {link.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Redes sociales de la automotora (Configuración → General). */}
          {redes.length > 0 && (
            <div>
              <h4
                className='text-sm font-semibold uppercase tracking-wider mb-4'
                style={{ color: headingColor }}
              >
                {socialColumnTitle}
              </h4>
              <ul className='space-y-3'>
                {redes.map((red) => (
                  <li key={red.platform}>
                    <a
                      href={red.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={handleSocialClick}
                      className='text-sm transition-colors duration-200 hover:opacity-80 inline-flex items-center gap-2'
                      style={{ color: textColor }}
                    >
                      <SocialIcon platform={red.platform} size={16} />
                      {red.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className='mt-10 pt-8' style={{ borderTop: `1px solid ${dividerColor}` }}>
          <p className='text-sm text-center' style={{ color: textColor }}>
            {finalCopyrightText}
          </p>
        </div>
      </div>
    </div>
  );
};

Footer.craft = {
  displayName: 'Footer',
  props: {
    columns: [
      { title: 'Empresa', links: [{ text: 'Sobre nosotros', url: '/about' }, { text: 'Contacto', url: '/contact' }] },
      { title: 'Servicios', links: [{ text: 'Financiamiento', url: '/financing' }, { text: 'Consignaciones', url: '/consignments' }] },
      { title: 'Vehículos', links: [{ text: 'Ver catálogo', url: '/vehicles' }] },
    ],
    bgColor: '#111827',
    textColor: '#9ca3af',
    headingColor: '#ffffff',
    dividerColor: 'rgba(255, 255, 255, 0.1)',
    socialIconBgColor: 'rgba(255, 255, 255, 0.08)',
    showSocial: true,
    socialLinks: [
      { platform: 'facebook', url: '' },
      { platform: 'instagram', url: '' },
      { platform: 'whatsapp', url: '' },
    ],
    copyrightText: '',
    logoUrl: '',
    columnsPerRow: 'auto',
    showSocialColumn: true,
    socialColumnTitle: 'Redes sociales',
  },
  rules: { canDrag: () => true, canDrop: () => true, canMoveIn: () => false },
};
